import api from "@/lib/axios";

// ----- Types -----

export type NotificationCategory =
  | "general"
  | "booking"
  | "payment"
  | "provider"
  | "system"
  | "promotion";

export interface Notification {
  id: number;
  title: string;
  message: string;
  category: NotificationCategory;
  action_url: string | null;
  is_read: boolean;
  read_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface NotificationPreferences {
  in_app_enabled: boolean;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  disabled_categories: NotificationCategory[];
}

export interface PaginatedNotificationResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Notification[];
}

export interface UnreadCountResponse {
  count: number;
}

// ----- API Functions -----

export async function fetchNotifications(
  page?: number,
  filters?: { is_read?: boolean; category?: NotificationCategory }
): Promise<PaginatedNotificationResponse> {
  const queryParams = new URLSearchParams();
  if (page) queryParams.set("page", String(page));
  if (filters?.is_read !== undefined)
    queryParams.set("is_read", String(filters.is_read));
  if (filters?.category) queryParams.set("category", filters.category);

  const query = queryParams.toString();
  const url = `/notifications/inbox/${query ? `?${query}` : ""}`;
  return api.get<PaginatedNotificationResponse>(url);
}

export async function fetchUnreadCount(): Promise<UnreadCountResponse> {
  return api.get<UnreadCountResponse>("/notifications/unread-count/");
}

export async function markAsRead(
  id: number
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(
    `/notifications/${id}/read/`
  );
}

export async function markAllAsRead(): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>("/notifications/read-all/");
}

export async function fetchPreferences(): Promise<NotificationPreferences> {
  return api.get<NotificationPreferences>(
    "/notifications/preferences/"
  );
}

export async function updatePreferences(
  data: NotificationPreferences
): Promise<NotificationPreferences> {
  return api.put<NotificationPreferences>(
    "/notifications/preferences/",
    data
  );
}
